import { useEffect, useState } from 'react';
import { TRACKS, CAREER_PATHS, TRACK_SALARY } from '../constants/appConstants.js';
import { supabase, fetchRoadmapProgress, getStreak, getRoadmapRoles, saveRoadmapRoles } from '../services/supabaseClient.js';
import SetRemindersModal from '../components/roadmap/SetRemindersModal.jsx';
import PersonalizationFlow from './onboarding/PersonalizationFlow.jsx';

// ─────────────────────────────────────────────────────────────────
// Roadmap — the learner's path from current track to career destination.
// Three zones: destination header, cert-path chain + career milestones
// (left), pace projection + M[Emory] acceleration (right).
// AdaptOS design system: white cards, amber CTAs, emerald progress,
// slate locked/meta, one dark M[Emory] card. No teal.
// ─────────────────────────────────────────────────────────────────

const AMBER = 'var(--brand-amber)';
const EMERALD = 'var(--brand-emerald)';
const SLATE = 'var(--brand-slate)';
const INK = '#1e1e2e';
const DAILY_MIN_DEFAULT = 30;

const shortLabel = (l) => (l || '').replace(/^(CompTIA|Microsoft|Google|AWS|Cisco)\s+/i, '');
const salaryFor  = (id) => TRACK_SALARY[id] || '$40k–$70k';
const pathFor    = (id) => CAREER_PATHS[id] || (id ? [id] : []);
const weeksToFinish = (remaining, dailyMin) => Math.max(1, Math.ceil((remaining * 20) / dailyMin / 7));

const cardWhite = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, boxShadow: 'var(--card-shadow)', padding: '20px 22px' };
const eyebrow   = { fontSize: 10, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase', color: SLATE, margin: 0 };

// ── Daily-goal ring ────────────────────────────────────────────
function GoalRing({ done, goal }) {
  const size = 92, stroke = 8, r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  const pct = goal > 0 ? Math.max(0, Math.min(1, done / goal)) : 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={EMERALD} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 16, fontWeight: 800, fill: INK }}>{done}/{goal}</text>
      <text x="50%" y="64%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 9, fontWeight: 700, fill: SLATE }}>min</text>
    </svg>
  );
}

function Pill({ text, color, bg }) {
  return <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color, background: bg, padding: '3px 9px', borderRadius: 99 }}>{text}</span>;
}

function Connector({ color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', paddingLeft: 22 }}>
      <div style={{ width: 0, height: 22, borderLeft: `2px dashed ${color}` }} />
    </div>
  );
}

// ── Certification-path card ────────────────────────────────────
function CertCard({ track, kind, pct, completed, total, weeks, prevLabel, onContinue, onDetails }) {
  const current = kind === 'current';
  const accent = current ? AMBER : SLATE;
  const badge = current ? 'In Progress' : kind === 'next' ? 'Up Next' : 'Future';
  return (
    <div style={{ ...cardWhite, borderLeft: `4px solid ${accent}`, opacity: current ? 1 : kind === 'next' ? 0.92 : 0.82, padding: '16px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: INK }}>{track.label}</span>
        <Pill text={badge} color={current ? AMBER : SLATE} bg={current ? '#fffaf0' : '#f1f3f7'} />
      </div>

      {current ? (
        <>
          <div style={{ height: 7, background: '#eef0f4', borderRadius: 99, overflow: 'hidden', margin: '12px 0 8px' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: EMERALD, borderRadius: 99 }} />
          </div>
          <p style={{ fontSize: 11, color: SLATE, margin: 0 }}>
            {pct}% complete · {completed} of {total} lessons · ~{weeks} {weeks === 1 ? 'week' : 'weeks'} at current pace
          </p>
          <button type="button" onClick={onContinue} style={linkAmber}>Continue →</button>
        </>
      ) : (
        <>
          <p style={{ fontSize: 11.5, color: SLATE, margin: '8px 0 0', lineHeight: 1.45 }}>
            Unlocks after {prevLabel} completion · ~{weeks} weeks estimated
          </p>
          <button type="button" onClick={onDetails} style={linkSlate}>View details →</button>
        </>
      )}
    </div>
  );
}

// ── Career-milestone node ──────────────────────────────────────
function MilestoneNode({ node, last }) {
  return (
    <div className="rm-milestone" style={{ display: 'flex', alignItems: 'flex-start', flex: '1 1 0', minWidth: 0 }}>
      <div style={{ flex: 1, minWidth: 0, textAlign: 'center', position: 'relative' }}>
        {!last && <div style={{ position: 'absolute', top: 9, left: '50%', width: '100%', height: 2, background: '#e5e7eb' }} />}
        <span style={{ position: 'relative', display: 'inline-block', width: 20, height: 20, borderRadius: '50%', background: node.dot, border: node.dotBorder ? `2px solid ${node.dot}` : 'none', zIndex: 1 }} />
        <p style={{ fontSize: 10, fontWeight: 700, color: SLATE, margin: '8px 0 0', lineHeight: 1.3 }}>{node.top}</p>
        <p style={{ fontSize: 13, fontWeight: 800, color: INK, margin: '3px 0 0', lineHeight: 1.25 }}>{node.role}</p>
        {node.salary && <p style={{ fontSize: 11, fontWeight: 700, color: node.salaryColor, margin: '2px 0 0' }}>{node.salary}</p>}
      </div>
    </div>
  );
}

export default function Roadmap({ learner, onNavigate, onLearnerUpdate }) {
  const trackId = learner?.current_track || 'comptia_aplus';
  const track   = TRACKS[trackId] || TRACKS.comptia_aplus;

  const [progress, setProgress] = useState(null);
  const [weekDays, setWeekDays] = useState(0);
  const [roadmapRoles, setRoadmapRoles] = useState([]);
  const [showReminders, setShowReminders] = useState(false);
  const [remindersSet,  setRemindersSet]  = useState(false);
  const [onboardStarted, setOnboardStarted] = useState(false);

  useEffect(() => {
    if (!learner?.id) return undefined;
    let cancelled = false;
    const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString();
    Promise.all([
      fetchRoadmapProgress(learner.id, trackId),
      getStreak(learner.id),
      supabase.from('learning_sessions').select('created_at').eq('learner_id', learner.id).gte('created_at', sevenDaysAgo),
      getRoadmapRoles(learner.id),
    ]).then(([prog, , sessionsRes, roles]) => {
      if (cancelled) return;
      setProgress(prog);
      const days = new Set();
      (sessionsRes?.data || []).forEach(r => { if (r.created_at) days.add(new Date(r.created_at).toDateString()); });
      setWeekDays(days.size);
      setRoadmapRoles(Array.isArray(roles) ? roles : []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [learner?.id, trackId]);

  const removeRole = (role) => {
    const next = roadmapRoles.filter(r => !(r.title === role.title && r.track_id === role.track_id));
    setRoadmapRoles(next);
    saveRoadmapRoles(learner?.id, next);
  };
  const rolesByTrack = (id) => roadmapRoles.filter(r => r.track_id === id);

  const careerTitle = learner?.career_goal || track.job_roles?.[0] || 'IT Professional';
  const onboarded = !!learner?.onboarding_completed_at;

  // Certification-path source, in priority order:
  //  1. Roles pinned on Career Hub → the full cert ladder behind each wanted
  //     role (expanded via CAREER_PATHS / pathFor), unioned — adapts to their
  //     goals (e.g. an Azure Administrator role → AZ-900 → AZ-104; an MS-102
  //     admin role → A+ → AB-900 → MS-102).
  //  2. Else the onboarding-recommended ladder (cert_path) — the default once
  //     the learner has completed onboarding.
  //  3. Else just the current track — with no onboarding and nothing pinned,
  //     the only default is the current track itself (e.g. A+ alone, no
  //     Network+/Security+ targeted).
  // The current track stays at the front to anchor the "You're here" progress.
  const onboardingPath = Array.isArray(learner?.cert_path) ? learner.cert_path.filter(Boolean) : [];
  const roleTrackIds   = [...new Set(roadmapRoles.map(r => r.track_id).filter(Boolean))];
  const goalTrackIds = roleTrackIds.length
    ? [...new Set(roleTrackIds.flatMap(pathFor))]
    : onboardingPath.length ? onboardingPath
    : pathFor(trackId);
  const chain = [...new Set([trackId, ...goalTrackIds].filter(Boolean))];

  const total     = progress?.total_count || 0;
  const completed = progress?.completed_count || 0;
  const remaining = Math.max(0, total - completed);
  const pct       = total > 0 ? Math.round((completed / total) * 100) : 0;
  const dailyMin  = learner?.daily_goal_minutes || DAILY_MIN_DEFAULT;
  const goalTarget = 40;
  const weeks     = weeksToFinish(remaining || 1, dailyMin);

  // M[Emory] acceleration message — forward-looking, never a gap callout.
  const nextRole = chain[1] ? (TRACKS[chain[1]]?.job_roles?.[0]) : null;
  let memoryMsg;
  if (weekDays > 0 && weekDays < 5) {
    memoryMsg = `Learners who study 5+ days a week finish ${shortLabel(track.label)} about 40% faster. You're at ${weekDays} ${weekDays === 1 ? 'day' : 'days'} this week — one more makes the curve bend.`;
  } else if (remaining > 0) {
    memoryMsg = `You're ${remaining} ${remaining === 1 ? 'lesson' : 'lessons'} from completing ${shortLabel(track.label)}${nextRole ? ` — the gateway to ${nextRole}` : ''}. At your pace you'll be there in about ${weeks} ${weeks === 1 ? 'week' : 'weeks'}.`;
  } else {
    memoryMsg = `${shortLabel(track.label)} is the strongest entry point on your path. Keep your streak alive and the next credential compounds on it.`;
  }

  // Career-milestone chain nodes.
  const nodes = [
    { dot: EMERALD, top: 'Start', role: 'Current', salary: null },
    ...chain.map((id, i) => {
      const pinned = rolesByTrack(id);
      return {
        dot: i === 0 ? AMBER : SLATE,
        top: `${shortLabel(TRACKS[id]?.label || id)} Complete`,
        role: pinned[0]?.title || TRACKS[id]?.job_roles?.[0] || '—',
        salary: pinned[0]?.salary || salaryFor(id),
        salaryColor: i === 0 ? EMERALD : SLATE,
      };
    }),
  ];

  return (
    <div style={{ maxWidth: 1180 }}>
      {/* On mobile the Career Milestones nodes shrank below their label width
          and the text overlapped. Give each node a fixed width and let the row
          scroll horizontally, preserving the timeline. !important overrides the
          inline flex: 1 1 0 on the nodes. */}
      <style>{`
        @media (max-width: 600px) {
          .rm-milestones {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 6px;
          }
          .rm-milestone { flex: 0 0 auto !important; width: 116px; }
        }
      `}</style>
      {/* Onboarding — optional, surfaced for learners who haven't set up */}
      {!onboarded && (
        <div style={{ ...cardWhite, borderLeft: `4px solid ${AMBER}`, marginBottom: 22 }}>
          <p style={{ ...eyebrow, color: AMBER }}>Personalize your path</p>
          <h3 style={{ fontSize: 18, fontWeight: 800, color: INK, margin: '7px 0 6px' }}>Set up your roadmap with M[Emory]</h3>
          <p style={{ fontSize: 13.5, color: SLATE, margin: 0, lineHeight: 1.55, maxWidth: 640 }}>
            A two-minute setup lets M[Emory] tailor your certification path, finish-date, and role recommendations to your goals — instead of generic defaults.
          </p>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', margin: '14px 0 16px' }}>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <p style={{ fontSize: 11, fontWeight: 800, color: INK, margin: 0 }}>What to expect</p>
              <p style={{ fontSize: 12.5, color: SLATE, margin: '3px 0 0', lineHeight: 1.45 }}>Your goal & timeline, experience level, and study pace — a few quick questions.</p>
            </div>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <p style={{ fontSize: 11, fontWeight: 800, color: INK, margin: 0 }}>How it helps</p>
              <p style={{ fontSize: 12.5, color: SLATE, margin: '3px 0 0', lineHeight: 1.45 }}>Sharper recommendations, an accurate finish-date, and a path built around the roles you want.</p>
            </div>
          </div>
          <button type="button" onClick={() => setOnboardStarted(true)}
            style={{ padding: '12px 24px', borderRadius: 10, background: AMBER, border: 'none', color: '#fff', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(217,119,6,0.26)' }}>
            Begin setup →
          </button>
        </div>
      )}

      {/* Personalization flow — launched from the card above; full-screen
          overlay. Gated only on onboardStarted (NOT !onboarded) so the flow's
          own completion screen survives after onLearnerUpdate flips onboarded
          true; it closes when the learner exits or starts their first lesson. */}
      {onboardStarted && (
        <PersonalizationFlow
          learner={learner}
          onLearnerUpdate={onLearnerUpdate}
          onStartLesson={() => { setOnboardStarted(false); onNavigate?.('study'); }}
          onExit={() => setOnboardStarted(false)}
        />
      )}

      {/* ZONE 1 — career destination header */}
      <div style={{ ...cardWhite, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', marginBottom: 22 }}>
        <span style={{ width: 56, height: 56, borderRadius: '50%', flexShrink: 0, background: 'rgba(217,119,6,0.12)', color: AMBER, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="10" r="3"/><path d="M12 21s-6-5.5-6-11a6 6 0 0 1 12 0c0 5.5-6 11-6 11z"/></svg>
        </span>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <p style={eyebrow}>Your path to</p>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: INK, margin: '3px 0 9px' }}>{careerTitle}</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Pill text="Strong demand" color={EMERALD} bg="rgba(5,150,105,0.12)" />
            <Pill text="+18% growth" color={EMERALD} bg="rgba(5,150,105,0.12)" />
            <Pill text={`Typical ${salaryFor(trackId)}`} color={SLATE} bg="#f1f3f7" />
          </div>
        </div>
        <button type="button" onClick={() => onNavigate?.('career_hub')} style={{ ...linkAmber, marginTop: 0, alignSelf: 'center' }}>Explore open roles →</button>
      </div>

      {/* ZONE 2 — two columns */}
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* LEFT */}
        <div style={{ flex: '1.4 1 420px', minWidth: 0 }}>
          <p style={{ ...eyebrow, marginBottom: 12 }}>Certification Path</p>

          {/* Branching graph: you → certs → roles (pinned from Career Hub) */}
          <div style={{ ...cardWhite, padding: '18px 20px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: EMERALD, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 800, color: INK }}>You're here</span>
              <span style={{ fontSize: 12, color: SLATE }}>· {shortLabel(track.label)}</span>
            </div>

            <div style={{ borderLeft: `2px dashed ${AMBER}`, marginLeft: 5, marginTop: 2 }}>
              {chain.map((id, i) => {
                const isCurrent = id === trackId;
                const roles = rolesByTrack(id);
                const accent = isCurrent ? AMBER : SLATE;
                return (
                  <div key={id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 0' }}>
                    <span style={{ width: 18, borderTop: `2px dashed ${accent}`, marginTop: 19, flexShrink: 0 }} />
                    {/* cert node */}
                    <div style={{ flexShrink: 0, width: 152, border: '1px solid #e5e7eb', borderLeft: `4px solid ${accent}`, borderRadius: 10, padding: '10px 12px', background: isCurrent ? '#fffaf0' : '#fff' }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: INK, margin: 0 }}>{shortLabel(TRACKS[id]?.label || id)}</p>
                      {isCurrent ? (
                        <>
                          <div style={{ height: 5, background: '#eef0f4', borderRadius: 99, overflow: 'hidden', margin: '7px 0 4px' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: EMERALD }} />
                          </div>
                          <p style={{ fontSize: 10, color: SLATE, margin: 0 }}>{pct}% · {completed}/{total} lessons</p>
                        </>
                      ) : (
                        <p style={{ fontSize: 10, fontWeight: 700, color: SLATE, margin: '5px 0 0', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          Targeted
                        </p>
                      )}
                    </div>
                    <span style={{ color: '#cbd2dc', fontSize: 16, fontWeight: 700, marginTop: 13, flexShrink: 0 }} aria-hidden>→</span>
                    {/* role leaves */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 4 }}>
                      {roles.length === 0 ? (
                        <button type="button" onClick={() => onNavigate?.('career_hub')} style={{ ...linkSlate, padding: '6px 0 0', fontSize: 12 }}>
                          + Add roles from Career Hub
                        </button>
                      ) : roles.map((r, ri) => (
                        <span key={ri} style={{ display: 'inline-flex', flexDirection: 'column', gap: 1, border: '1px solid #e5e7eb', borderRadius: 9, padding: '6px 10px', background: '#fff' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: INK }}>{r.title}</span>
                          {r.salary && <span style={{ fontSize: 10.5, fontWeight: 700, color: EMERALD }}>{r.salary}/yr</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <button type="button" onClick={() => onNavigate?.('study')} style={linkAmber}>Continue current track →</button>
          </div>

          {/* Career milestones */}
          <div style={{ ...cardWhite, marginTop: 20 }}>
            <p style={{ ...eyebrow, marginBottom: 18 }}>Career Milestones</p>
            <div className="rm-milestones" style={{ display: 'flex', gap: 6 }}>
              {nodes.map((n, i) => <MilestoneNode key={i} node={n} last={i === nodes.length - 1} />)}
            </div>
            <p style={{ fontSize: 12, color: SLATE, margin: '18px 0 0', lineHeight: 1.5 }}>
              {shortLabel(track.label)} is the strongest entry point. Explore roles you qualify for at each milestone.
            </p>
            <button type="button" onClick={() => onNavigate?.('career_hub')} style={linkAmber}>View Career Hub →</button>
          </div>
        </div>

        {/* RIGHT */}
        <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Your Pace */}
          <div style={cardWhite}>
            <p style={{ ...eyebrow, marginBottom: 14 }}>Your Pace</p>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                <p style={{ fontSize: 13, color: INK, margin: 0, lineHeight: 1.5 }}>
                  🕐 At {dailyMin} min/day, you'll complete {shortLabel(track.label)} in approximately <strong>{weeks} {weeks === 1 ? 'week' : 'weeks'}</strong>.
                </p>
                <p style={{ fontSize: 11, color: SLATE, margin: '8px 0 0', fontStyle: 'italic', lineHeight: 1.5 }}>
                  Studying {weekDays} of 7 days this week — increase to 5 days to finish ~40% faster.
                </p>
              </div>
              <div style={{ flex: '0 0 auto', textAlign: 'center' }}>
                <GoalRing done={dailyMin} goal={goalTarget} />
                <p style={{ fontSize: 10, fontWeight: 700, color: SLATE, margin: '4px 0 0', textTransform: 'uppercase', letterSpacing: 0.4 }}>Daily Goal</p>
              </div>
            </div>
            {remindersSet ? (
              <p style={{ fontSize: 13, fontWeight: 700, color: EMERALD, margin: '16px 0 0', textAlign: 'center' }}>Reminders set ✓</p>
            ) : (
              <button type="button" onClick={() => setShowReminders(true)}
                style={{ width: '100%', marginTop: 16, padding: '12px', borderRadius: 10, background: AMBER, border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(217,119,6,0.26)' }}>
                🔔  Set Reminders
              </button>
            )}
          </div>

          {/* M[Emory] acceleration */}
          <div style={{ background: '#1e1e2e', borderRadius: 14, padding: '20px 22px', boxShadow: '0 6px 24px rgba(15,23,42,0.18)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: EMERALD, boxShadow: '0 0 0 3px rgba(5,150,105,0.25)' }} />
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: EMERALD }}>M[Emory]</span>
            </span>
            <p style={{ fontSize: 14, color: '#e8e6e1', margin: '12px 0 0', lineHeight: 1.55 }}>{memoryMsg}</p>
            <button type="button" onClick={() => onNavigate?.('career_hub')}
              style={{ background: 'transparent', border: 'none', color: EMERALD, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: '12px 0 0' }}>
              View full path insights →
            </button>
          </div>

          {/* Selected Roles — pinned from Career Hub, own scroll */}
          <div style={cardWhite}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <p style={eyebrow}>Selected Roles</p>
              <span style={{ fontSize: 11, fontWeight: 700, color: roadmapRoles.length >= 9 ? AMBER : SLATE }}>{roadmapRoles.length}/9</span>
            </div>
            {roadmapRoles.length === 0 ? (
              <p style={{ fontSize: 12.5, color: SLATE, margin: 0, lineHeight: 1.5 }}>
                Pin roles from{' '}
                <button type="button" onClick={() => onNavigate?.('career_hub')} style={{ background: 'none', border: 'none', color: AMBER, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, padding: 0 }}>Career Hub</button>
                {' '}to build your path — up to 3 per certification.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 232, overflowY: 'auto', paddingRight: 4 }}>
                {roadmapRoles.map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid #e5e7eb', borderRadius: 9, padding: '9px 11px' }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 12.5, fontWeight: 700, color: INK, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</p>
                      <p style={{ fontSize: 10.5, color: SLATE, margin: '2px 0 0' }}>
                        {shortLabel(TRACKS[r.track_id]?.label || r.track_id)}{r.salary ? ` · ${r.salary}/yr` : ''}
                      </p>
                    </div>
                    <button type="button" onClick={() => removeRole(r)} aria-label="Remove role"
                      style={{ width: 24, height: 24, borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', color: SLATE, fontSize: 15, lineHeight: 1, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0, padding: 0 }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showReminders && (
        <SetRemindersModal
          learner={learner}
          onClose={() => setShowReminders(false)}
          onSaved={() => { setRemindersSet(true); setShowReminders(false); }}
        />
      )}
    </div>
  );
}

const linkAmber = { background: 'transparent', border: 'none', color: AMBER, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: '12px 0 0', display: 'inline-block' };
const linkSlate = { background: 'transparent', border: 'none', color: SLATE, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: '10px 0 0', display: 'inline-block' };
